import { ReactNode, useEffect, useState, useMemo } from 'react';

// third-party
import { IntlProvider, MessageFormatElement } from 'react-intl';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { enUS } from 'date-fns/locale/en-US';
import type { Locale } from 'date-fns';

// project-imports
import useConfig from 'hooks/useConfig';

// types
import { I18n } from 'types/config';

// map i18n config to date-fns locale
const dateFnsLocaleMap: Record<I18n, Locale> = {
  'pt-BR': ptBR,
  en: enUS
};

// load locale files
const loadLocaleData = (locale: I18n) => {
  switch (locale) {
    case 'en':
      return import('utils/locales/en.json');
    case 'pt-BR':
    default:
      return import('utils/locales/pt-BR.json');
  }
};

interface Props {
  children: ReactNode;
}

// ==============================|| LOCALIZATION ||============================== //

export default function Locales({ children }: Props) {
  const { i18n } = useConfig();

  const [messages, setMessages] = useState<Record<string, string> | Record<string, MessageFormatElement[]> | undefined>();
  const localeDataPromise = useMemo(() => loadLocaleData(i18n), [i18n]);
  const dateFnsLocale = useMemo(() => dateFnsLocaleMap[i18n] ?? ptBR, [i18n]);

  useEffect(() => {
    localeDataPromise.then((d: { default: Record<string, string> | Record<string, MessageFormatElement[]> | undefined }) => {
      setMessages(d.default);
    });
  }, [localeDataPromise]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = i18n;
    }
  }, [i18n]);

  return (
    <>
      {messages && (
        <IntlProvider locale={i18n === 'pt-BR' ? 'pt' : i18n} defaultLocale="pt" messages={messages}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
            {children}
          </LocalizationProvider>
        </IntlProvider>
      )}
    </>
  );
}
